import React, { useEffect, useState, useCallback } from "react";
import { getTrainingDays, getTrainingDetails, moveTraining } from "../../services/authService";

const DAY_NAMES = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function startOfWeek(date) {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	const day = d.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	d.setDate(d.getDate() + diff);
	return d;
}

function addDays(date, n) {
	const d = new Date(date);
	d.setDate(d.getDate() + n);
	return d;
}

// Renvoie la date au format YYYY-MM-DD en HEURE LOCALE (évite le décalage
// de fuseau que provoquerait toISOString().split("T")[0]).
function localISO(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

async function fetchWeekTrainings(studentId, monday) {
	const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
	const monthCache = new Map();
	const result = new Map(); // key = ISO date → trainings[]

	for (const day of days) {
		const y = day.getFullYear();
		const m = day.getMonth() + 1;
		const mKey = `${y}-${m}`;
		if (!monthCache.has(mKey)) {
			const r = await getTrainingDays(studentId, y, m);
			monthCache.set(mKey, r.success ? (r.trainingDays || []) : []);
		}
		const key = localISO(day);
		result.set(key, []);
		if (!monthCache.get(mKey).includes(day.getDate())) continue;
		const details = await getTrainingDetails(studentId, y, m, day.getDate());
		if (details.success) {
			(details.trainings || []).forEach((t) => {
				if (t.estTemplate || t.EstTemplate) return;
				result.get(key).push(t);
			});
		}
	}
	return result;
}

export default function CalendarWeekView({
	studentId,
	anchorDate,     // Date de référence dans la semaine à afficher
	onPrevWeek,
	onNextWeek,
	onToday,
	onOpenTraining,
	onCreateOnDay,   // (dateTs) => void — bouton + sur un jour vide
	refreshKey = 0,  // change pour forcer un refetch
}) {
	const [monday, setMonday] = useState(() => startOfWeek(anchorDate || new Date()));
	const [trainingsByDay, setTrainingsByDay] = useState(new Map());
	const [loading, setLoading] = useState(false);
	const [draggingId, setDraggingId] = useState(null);
	const [dropTarget, setDropTarget] = useState(null); // ISO date

	// Aligne la semaine sur anchorDate
	useEffect(() => {
		setMonday(startOfWeek(anchorDate || new Date()));
	}, [anchorDate]);

	// Chargement
	const load = useCallback(async () => {
		if (!studentId) { setTrainingsByDay(new Map()); return; }
		setLoading(true);
		const res = await fetchWeekTrainings(studentId, monday);
		setTrainingsByDay(res);
		setLoading(false);
	}, [studentId, monday]);

	useEffect(() => { load(); }, [load, refreshKey]);

	const handlePrev = () => {
		const prev = addDays(monday, -7);
		setMonday(prev);
		onPrevWeek?.(prev);
	};
	const handleNext = () => {
		const next = addDays(monday, 7);
		setMonday(next);
		onNextWeek?.(next);
	};
	const handleToday = () => {
		const t = startOfWeek(new Date());
		setMonday(t);
		onToday?.(t);
	};

	const handleDragStart = (e, training, sourceISO) => {
		setDraggingId(training.id);
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", JSON.stringify({ id: training.id, from: sourceISO }));
	};

	const handleDragEnd = () => {
		setDraggingId(null);
		setDropTarget(null);
	};

	const handleDragOver = (e, targetISO) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		setDropTarget(targetISO);
	};

	const handleDrop = async (e, targetISO) => {
		e.preventDefault();
		setDropTarget(null);
		let payload = null;
		try { payload = JSON.parse(e.dataTransfer.getData("text/plain")); } catch (_) {}
		if (!payload || payload.from === targetISO) { setDraggingId(null); return; }
		const target = new Date(targetISO + "T12:00:00").getTime();
		// Optimistic UI: remove from source, add to target
		setTrainingsByDay((prev) => {
			const next = new Map(prev);
			const src = (next.get(payload.from) || []).filter((t) => t.id !== payload.id);
			const moved = (prev.get(payload.from) || []).find((t) => t.id === payload.id);
			next.set(payload.from, src);
			if (moved) next.set(targetISO, [...(next.get(targetISO) || []), moved]);
			return next;
		});
		setDraggingId(null);
		const res = await moveTraining(studentId, payload.id, target);
		if (!res.success) {
			// Reload on failure
			await load();
		}
	};

	const days = Array.from({ length: 7 }, (_, i) => {
		const d = addDays(monday, i);
		const iso = localISO(d);
		const isToday = (() => {
			const now = new Date();
			return now.toDateString() === d.toDateString();
		})();
		return {
			iso,
			label: DAY_NAMES[i],
			dayNum: d.getDate(),
			month: d.toLocaleDateString("fr-FR", { month: "short" }),
			isToday,
			trainings: trainingsByDay.get(iso) || [],
		};
	});

	const rangeLabel = (() => {
		const sunday = addDays(monday, 6);
		const opt = { day: "2-digit", month: "short" };
		return `${monday.toLocaleDateString("fr-FR", opt)} → ${sunday.toLocaleDateString("fr-FR", opt)}`;
	})();

	return (
		<div className="calendar-week-view">
			<div className="calendar-week-view-head">
				<div className="calendar-week-view-nav">
					<button type="button" onClick={handlePrev} aria-label="Semaine précédente">‹</button>
					<button type="button" onClick={handleToday} className="today-btn">Aujourd'hui</button>
					<button type="button" onClick={handleNext} aria-label="Semaine suivante">›</button>
				</div>
				<div className="calendar-week-view-title">
					Semaine du {rangeLabel}
				</div>
				{loading && <span className="calendar-week-view-loading">Chargement…</span>}
			</div>

			<div className="calendar-week-view-grid">
				{days.map((d) => (
					<div
						key={d.iso}
						className={`calendar-week-view-col ${d.isToday ? "today" : ""} ${dropTarget === d.iso ? "drop-target" : ""}`}
						onDragOver={(e) => handleDragOver(e, d.iso)}
						onDrop={(e) => handleDrop(e, d.iso)}
						onDragLeave={() => setDropTarget((prev) => prev === d.iso ? null : prev)}
					>
						<div className="calendar-week-view-col-head">
							<span className="calendar-week-view-col-dow">{d.label}</span>
							<span className="calendar-week-view-col-day">{d.dayNum}</span>
							<span className="calendar-week-view-col-month">{d.month}</span>
						</div>
						<div className="calendar-week-view-col-body">
							{d.trainings.length === 0 ? (
								<button
									className="calendar-week-view-empty"
									onClick={() => onCreateOnDay?.(new Date(d.iso + "T12:00:00").getTime())}
									title="Créer une séance"
								>+ séance</button>
							) : d.trainings.map((t) => {
								const exos = (t.exercices || []).filter((ex) => ex.supprime !== 1);
								return (
									<div
										key={t.id}
										draggable
										onDragStart={(e) => handleDragStart(e, t, d.iso)}
										onDragEnd={handleDragEnd}
										className={`calendar-week-view-card ${draggingId === t.id ? "dragging" : ""}`}
										onClick={() => onOpenTraining?.(t.id)}
										title="Cliquer pour ouvrir · Glisser pour déplacer"
									>
										<div className="calendar-week-view-card-name">{t.nom || "Séance"}</div>
										<div className="calendar-week-view-card-meta">
											{exos.length} exo{exos.length > 1 ? "s" : ""}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
